import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Building2, Plus, RefreshCw, Sparkles, TrendingUp, Users } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = ['prospecto', 'onboarding', 'activo', 'escalando', 'pausado'];

const MetricCard = ({ icon: Icon, label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
        <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
        <p className="mt-2 text-xs text-text-muted">{helper}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="w-5 h-5" /></div>
    </div>
  </div>
);

export default function CompanyCEOHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    industry: '',
    website: '',
    contact_name: '',
    contact_email: '',
    company_stage: 'prospecto',
    primary_goal: '',
    desired_services: 'ventas,marketing',
    ai_categories: 'contenido,video,automatizacion',
    channels: 'instagram,facebook,tiktok',
    assigned_agents: 'VENTAS,CONTENIDO,ADMIN',
    notes: '',
    estimated_monthly_value: ''
  });

  const loadHub = async () => {
    try {
      const res = await authFetch(`${API}/admin/companies-hub`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el CEO Hub');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el CEO Hub');
    }
  };

  useEffect(() => {
    loadHub();
  }, []);

  const topService = useMemo(() => hub?.top_services?.[0]?.name || 'ventas', [hub]);

  const createCompany = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const res = await authFetch(`${API}/admin/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          desired_services: form.desired_services.split(',').map((item) => item.trim()).filter(Boolean),
          ai_categories: form.ai_categories.split(',').map((item) => item.trim()).filter(Boolean),
          channels: form.channels.split(',').map((item) => item.trim()).filter(Boolean),
          assigned_agents: form.assigned_agents.split(',').map((item) => item.trim()).filter(Boolean),
          estimated_monthly_value: form.estimated_monthly_value ? Number(form.estimated_monthly_value) : null,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear la empresa');
      toast.success('Empresa añadida al CEO Hub');
      setForm({
        company_name: '', industry: '', website: '', contact_name: '', contact_email: '', company_stage: 'prospecto',
        primary_goal: '', desired_services: 'ventas,marketing', ai_categories: 'contenido,video,automatizacion',
        channels: 'instagram,facebook,tiktok', assigned_agents: 'VENTAS,CONTENIDO,ADMIN', notes: '', estimated_monthly_value: ''
      });
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo crear la empresa');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStage = async (companyId, companyStage) => {
    try {
      const res = await authFetch(`${API}/admin/companies/${companyId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_stage: companyStage })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar el estado');
      toast.success('Estado actualizado');
      setHub((prev) => ({ ...prev, companies: prev.companies.map((item) => item.id === companyId ? data.company : item) }));
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar el estado');
    }
  };

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="ceo-hub-loading">Cargando CEO Hub...</div>;

  return (
    <div className="space-y-6" data-testid="ceo-hub">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Hub central multiempresa</p>
          <h3 className="mt-2 text-2xl font-semibold text-text-heading">CEO Hub · control de empresas, agentes y oportunidades</h3>
          <p className="mt-2 max-w-3xl text-sm text-text-body">Base para gestionar diferentes empresas dentro del ecosistema, ver qué servicios facturan más y recomendar la mejor combinación de agentes, IA y automatizaciones.</p>
        </div>
        <button onClick={loadHub} className="btn-secondary" data-testid="ceo-hub-refresh-button"><RefreshCw className="w-4 h-4" /> Refrescar</button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={Building2} label="Empresas" value={hub.metrics.total_companies} helper="Base multiempresa" testId="ceo-hub-metric-companies" />
        <MetricCard icon={Users} label="Activas" value={hub.metrics.active_companies} helper="En operación o escalado" testId="ceo-hub-metric-active" />
        <MetricCard icon={TrendingUp} label="Pipeline mensual" value={`$${hub.metrics.monthly_pipeline_value}`} helper="Valor potencial estimado" testId="ceo-hub-metric-value" />
        <MetricCard icon={Sparkles} label="Foco top" value={topService} helper="Servicio más demandado" testId="ceo-hub-metric-focus" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.6fr_1fr]">
        <form onSubmit={createCompany} className="glass-card p-6 space-y-4" data-testid="ceo-hub-create-form">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Nueva empresa</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Alta rápida</h4>
            </div>
            <Plus className="w-5 h-5 text-primary" />
          </div>
          <input className="input-kinesthetic" placeholder="Empresa" value={form.company_name} onChange={(e) => setForm((prev) => ({ ...prev, company_name: e.target.value }))} data-testid="ceo-company-name-input" />
          <input className="input-kinesthetic" placeholder="Industria" value={form.industry} onChange={(e) => setForm((prev) => ({ ...prev, industry: e.target.value }))} data-testid="ceo-company-industry-input" />
          <input className="input-kinesthetic" placeholder="Web" value={form.website} onChange={(e) => setForm((prev) => ({ ...prev, website: e.target.value }))} data-testid="ceo-company-website-input" />
          <div className="grid gap-4 md:grid-cols-2">
            <input className="input-kinesthetic" placeholder="Contacto" value={form.contact_name} onChange={(e) => setForm((prev) => ({ ...prev, contact_name: e.target.value }))} data-testid="ceo-company-contact-input" />
            <input className="input-kinesthetic" placeholder="Email" value={form.contact_email} onChange={(e) => setForm((prev) => ({ ...prev, contact_email: e.target.value }))} data-testid="ceo-company-email-input" />
          </div>
          <select className="input-kinesthetic" value={form.company_stage} onChange={(e) => setForm((prev) => ({ ...prev, company_stage: e.target.value }))} data-testid="ceo-company-stage-select">
            {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
          </select>
          <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Objetivo principal" value={form.primary_goal} onChange={(e) => setForm((prev) => ({ ...prev, primary_goal: e.target.value }))} data-testid="ceo-company-goal-input" />
          <input className="input-kinesthetic" placeholder="Servicios deseados (coma)" value={form.desired_services} onChange={(e) => setForm((prev) => ({ ...prev, desired_services: e.target.value }))} data-testid="ceo-company-services-input" />
          <input className="input-kinesthetic" placeholder="Categorías IA (coma)" value={form.ai_categories} onChange={(e) => setForm((prev) => ({ ...prev, ai_categories: e.target.value }))} data-testid="ceo-company-ai-input" />
          <input className="input-kinesthetic" placeholder="Canales (coma)" value={form.channels} onChange={(e) => setForm((prev) => ({ ...prev, channels: e.target.value }))} data-testid="ceo-company-channels-input" />
          <input className="input-kinesthetic" placeholder="Agentes asignados (coma)" value={form.assigned_agents} onChange={(e) => setForm((prev) => ({ ...prev, assigned_agents: e.target.value }))} data-testid="ceo-company-agents-input" />
          <input className="input-kinesthetic" placeholder="Valor mensual estimado" value={form.estimated_monthly_value} onChange={(e) => setForm((prev) => ({ ...prev, estimated_monthly_value: e.target.value }))} data-testid="ceo-company-value-input" />
          <textarea className="input-kinesthetic resize-none" rows={3} placeholder="Notas" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="ceo-company-notes-input" />
          <button type="submit" className="btn-primary w-full justify-center" disabled={submitting} data-testid="ceo-company-submit-button">
            <Plus className="w-4 h-4" /> {submitting ? 'Guardando...' : 'Añadir empresa'}
          </button>
        </form>

        <div className="space-y-4" data-testid="ceo-hub-company-list">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="glass-card p-5" data-testid="ceo-hub-top-services">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Lo que más se pide</p>
              <h4 className="mt-2 text-lg font-semibold text-text-heading">Servicios top</h4>
              <div className="mt-4 space-y-3">
                {hub.top_services.length ? hub.top_services.map((item) => <div key={item.name} className="flex items-center justify-between text-sm"><span className="text-text-body">{item.name}</span><span className="text-primary font-semibold">{item.count}</span></div>) : <p className="text-sm text-text-muted">Aún no hay demanda registrada.</p>}
              </div>
            </div>
            <div className="glass-card p-5" data-testid="ceo-hub-top-ai">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">IA más pedida</p>
              <h4 className="mt-2 text-lg font-semibold text-text-heading">Categorías top</h4>
              <div className="mt-4 space-y-3">
                {hub.top_ai_categories.length ? hub.top_ai_categories.map((item) => <div key={item.name} className="flex items-center justify-between text-sm"><span className="text-text-body">{item.name}</span><span className="text-primary font-semibold">{item.count}</span></div>) : <p className="text-sm text-text-muted">Aún no hay categorías registradas.</p>}
              </div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {hub.companies.map((company) => (
              <div key={company.id} className="glass-card p-5" data-testid={`ceo-company-card-${company.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{company.industry}</p>
                    <h4 className="mt-2 text-xl font-semibold text-text-heading">{company.company_name}</h4>
                    <p className="mt-2 text-sm text-text-body">{company.primary_goal}</p>
                  </div>
                  <select className="input-kinesthetic min-w-[140px]" value={company.company_stage} onChange={(e) => updateStage(company.id, e.target.value)} data-testid={`ceo-company-stage-${company.id}`}>
                    {STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 text-sm text-text-body">
                  <div>
                    <p className="text-text-muted">Contacto</p>
                    <p>{company.contact_name}</p>
                    <p className="text-xs text-text-muted mt-1">{company.contact_email}</p>
                  </div>
                  <div>
                    <p className="text-text-muted">Valor estimado</p>
                    <p>${company.estimated_monthly_value || 0} / mes</p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Stack recomendado</p>
                    <div className="flex flex-wrap gap-2">{(company.recommended_stack || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}</div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Agentes</p>
                    <div className="flex flex-wrap gap-2">{(company.assigned_agents || []).map((agent) => <span key={agent} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{agent}</span>)}</div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-muted mb-2">Servicios y canales</p>
                    <p className="text-sm text-text-body">{(company.desired_services || []).join(', ') || '—'}</p>
                    <p className="text-xs text-text-muted mt-1">{(company.channels || []).join(', ') || 'Sin canales definidos'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}